import { useEffect, useRef, useState } from 'react';
import {
  ensureNetworkNoticesReady,
  isNetworkNoticesReady,
  markNetworkNoticesReady,
  NETWORK_NOTICES_READY_EVENT,
} from './loading/dismissAppBoot';
import './network-notice.css';

const HIDE_MS = 4200;
const POOR_CHECK_MS = 20000;
const POOR_RTT_MS = 900;

const COPY = {
  offline: 'İnternet bağlantınız yok.',
  online: 'Tekrar onlinesınız.',
  poor: 'Zayıf internet bağlantısı.',
};

/** Lucide Icons (MIT) — https://lucide.dev/icons/wifi */
function WifiIcon() {
  return (
    <svg className="vb-net-icon__svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M12 20h.01" />
      <path d="M2 8.82a15 15 0 0 1 20 0" />
      <path d="M5 12.859a10 10 0 0 1 14 0" />
      <path d="M8.5 16.429a5 5 0 0 1 7 0" />
    </svg>
  );
}

/** Lucide Icons (MIT) — https://lucide.dev/icons/wifi-off */
function WifiOffIcon() {
  return (
    <svg className="vb-net-icon__svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M12 20h.01" />
      <path d="M8.5 16.429a5 5 0 0 1 7 0" />
      <path d="M5 12.859a10 10 0 0 1 5.17-2.69" />
      <path d="M19 12.859a10 10 0 0 0-2.007-1.523" />
      <path d="M2 8.82a15 15 0 0 1 4.177-2.643" />
      <path d="M22 8.82a15 15 0 0 0-11.288-3.764" />
      <path d="m2 2 20 20" />
    </svg>
  );
}

/** Lucide Icons (MIT) — https://lucide.dev/icons/wifi-low */
function WifiLowIcon() {
  return (
    <svg className="vb-net-icon__svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M12 20h.01" />
      <path d="M8.5 16.429a5 5 0 0 1 7 0" />
    </svg>
  );
}

/** Lucide Icons (MIT) — https://lucide.dev/icons/x */
function CloseIcon() {
  return (
    <svg className="vb-net-close__svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

function StatusIcon({ kind }) {
  const Icon = kind === 'online' ? WifiIcon : kind === 'poor' ? WifiLowIcon : WifiOffIcon;
  return (
    <span className={`vb-net-icon vb-net-icon--${kind}`} aria-hidden="true">
      <span className="vb-net-icon__halo" />
      <span className="vb-net-icon__core">
        <Icon />
      </span>
    </span>
  );
}

function connectionLooksPoor() {
  try {
    const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c) return false;
    const type = String(c.effectiveType || '').toLowerCase();
    if (type === 'slow-2g' || type === '2g') return true;
    if (Number(c.rtt) >= POOR_RTT_MS) return true;
    if (c.saveData) return true;
  } catch {
    /* ignore */
  }
  return false;
}

export default function NetworkStatusNotice() {
  const [notice, setNotice] = useState(null);
  const [leaving, setLeaving] = useState(false);
  const hideTimer = useRef(null);
  const leaveTimer = useRef(null);
  // Only track offline transitions after the app has fully booted.
  const wasOffline = useRef(false);
  const sawOnlineAfterReady = useRef(false);
  const poorShown = useRef(false);
  const armed = useRef(false);

  const clearTimers = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
    hideTimer.current = null;
    leaveTimer.current = null;
  };

  const dismiss = () => {
    clearTimers();
    setLeaving(true);
    leaveTimer.current = setTimeout(() => {
      setNotice(null);
      setLeaving(false);
    }, 280);
  };

  const show = (kind, { sticky = false } = {}) => {
    if (!armed.current) return;
    clearTimers();
    setLeaving(false);
    setNotice(kind);
    if (sticky) return;
    hideTimer.current = setTimeout(dismiss, HIDE_MS);
  };

  useEffect(() => {
    let cleaned = false;
    let interval = 0;
    let conn = null;
    let poll = 0;

    const onOffline = () => {
      // Ignore offline blips until we have confirmed online after boot.
      if (!armed.current || !sawOnlineAfterReady.current) return;
      wasOffline.current = true;
      poorShown.current = false;
      show('offline', { sticky: true });
    };

    const onOnline = () => {
      if (!armed.current) return;
      sawOnlineAfterReady.current = true;
      if (wasOffline.current) {
        wasOffline.current = false;
        show('online');
      }
    };

    const checkPoor = () => {
      if (!armed.current || !sawOnlineAfterReady.current) return;
      if (!navigator.onLine || wasOffline.current) return;
      if (!connectionLooksPoor()) {
        poorShown.current = false;
        return;
      }
      if (poorShown.current) return;
      poorShown.current = true;
      show('poor');
    };

    const arm = () => {
      if (cleaned || armed.current) return;
      armed.current = true;
      markNetworkNoticesReady();

      // Establish baseline without toasting load-time flakiness.
      if (navigator.onLine) {
        sawOnlineAfterReady.current = true;
        wasOffline.current = false;
      } else {
        sawOnlineAfterReady.current = false;
        wasOffline.current = false;
      }

      window.addEventListener('offline', onOffline);
      window.addEventListener('online', onOnline);

      try {
        conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        conn?.addEventListener?.('change', checkPoor);
      } catch {
        /* ignore */
      }

      interval = window.setInterval(checkPoor, POOR_CHECK_MS);
    };

    const tryArm = () => {
      ensureNetworkNoticesReady();
      if (isNetworkNoticesReady()) arm();
    };

    if (isNetworkNoticesReady()) {
      arm();
    } else {
      window.addEventListener(NETWORK_NOTICES_READY_EVENT, tryArm);
      poll = window.setInterval(tryArm, 250);
      tryArm();
    }

    return () => {
      cleaned = true;
      clearTimers();
      window.removeEventListener(NETWORK_NOTICES_READY_EVENT, tryArm);
      if (poll) window.clearInterval(poll);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
      conn?.removeEventListener?.('change', checkPoor);
      if (interval) window.clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!notice) return null;

  const contentClass =
    notice === 'online'
      ? 'tb--notice-network_online'
      : notice === 'poor'
        ? 'tb--notice-network_poor'
        : 'tb--notice-network_offline';

  return (
    <div className="tb--notice-wrapper vb-network-notice" aria-live="polite">
      <div className={`tb--notice-container${leaving ? ' move-up-leave' : ''}`}>
        <div
          className={`tb--notice-content ${contentClass} tb--flex tb--justify-between tb--align-center`}
        >
          <div className="tb--flex tb--align-center">
            <StatusIcon kind={notice} />
            <span className="tb--notice-msg">{COPY[notice]}</span>
          </div>
          <button type="button" className="vb-net-close" aria-label="Kapat" onClick={dismiss}>
            <CloseIcon />
          </button>
        </div>
      </div>
    </div>
  );
}
